// analytics/hooks/useOpenVisits.ts — SKIN-695
import { useState, useEffect } from 'react';
import axios from 'axios';
import { clinicalServices } from '../services/endpoints/clinical';
import { OpenVisitsRow } from '../OpenVisits';

// Mirrors useClinicalData (SKIN-260 cancellation model): axios CancelToken, not an
// AbortSignal, and a cancelled run is not a failure.
export const useOpenVisits = (investigation: { uuid: string } | undefined) => {
    const [data, setData] = useState<OpenVisitsRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [reloadToken, setReloadToken] = useState(0);

    const retry = () => setReloadToken(t => t + 1);

    useEffect(() => {
        const source = axios.CancelToken.source();
        const superseded = () => Boolean(source.token.reason);

        const fetchData = async () => {
            if (!investigation?.uuid) return;
            setLoading(true);
            setError(null);
            try {
                const rows = await clinicalServices.getOpenVisits(investigation.uuid, source.token);
                if (superseded()) return;
                setData(rows);
            } catch (err) {
                if (axios.isCancel(err) || superseded()) return;
                setError(err instanceof Error ? err : new Error('Unknown error'));
            } finally {
                if (!superseded()) setLoading(false);
            }
        };

        fetchData();
        return () => source.cancel('superseded');
    }, [investigation?.uuid, reloadToken]);

    return { data, loading, error, retry };
};
